import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Drawer.module.css';

export interface DrawerProps {
  /** Whether the drawer is open. Controlled. */
  open: boolean;
  /** Called when the drawer requests to close (Esc, scrim click, close button). */
  onClose: () => void;
  /** Drawer title (rendered in the header; also wired to aria-labelledby). */
  title: ReactNode;
  /** Optional subtitle line under the title. */
  subtitle?: ReactNode;
  /** Drawer width in pixels. Default 480 (per Users handoff). */
  width?: number;
  /** Whether clicking the scrim closes the drawer. Default true. */
  closeOnScrimClick?: boolean;
  /** Whether pressing Escape closes the drawer. Default true. */
  closeOnEsc?: boolean;
  /** Drawer body. */
  children: ReactNode;
  /** Optional sticky footer (e.g. primary/cancel buttons). */
  footer?: ReactNode;
  /** Optional extra className on the panel. */
  className?: string;
}

/**
 * Right-side overlay panel with scrim. Used by:
 *  - InviteUserDrawer
 *  - UserDetailDrawer
 *  - Future admin surfaces with side-panel detail/edit flows
 *
 * Accessibility:
 *  - role="dialog", aria-modal, aria-labelledby on the title
 *  - Esc + scrim click close (configurable)
 *  - Focus trap: focus moves to the close button on open, returns to the
 *    trigger element on close. Tab cycles within the panel.
 *  - Sticky title + footer (if provided); body scrolls between them.
 *
 * Rendered via React portal to document.body so it escapes any stacking
 * context the trigger lives in.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  width = 480,
  closeOnScrimClick = true,
  closeOnEsc = true,
  children,
  footer,
  className,
}: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<Element | null>(null);

  // Capture the element that had focus when the drawer opened, so we can
  // restore it on close (a11y best practice).
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    // Move focus into the drawer on open. The close button is a stable
    // first target; if it isn't rendered yet, the panel itself takes focus.
    const id = window.requestAnimationFrame(() => {
      const closeBtn = panelRef.current?.querySelector<HTMLElement>('[data-drawer-close]');
      (closeBtn ?? panelRef.current)?.focus();
    });
    return () => {
      window.cancelAnimationFrame(id);
      // Restore focus on close. Safe-cast since the element may have been
      // removed; HTMLElement.focus is a no-op then.
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [open]);

  // Body scroll lock while open. Avoids the page scrolling behind the scrim
  // when the user mousewheels.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Esc handler at the document level so it fires no matter which descendant
  // currently has focus.
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeOnEsc, onClose]);

  const onScrimClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!closeOnScrimClick) return;
      // Only count clicks on the scrim itself, not bubbled from the panel.
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnScrimClick, onClose],
  );

  // Focus trap: cycle Tab within the panel.
  const onPanelKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;
  // SSR guard — createPortal needs a real DOM. Component-level apps (Vite)
  // always have one, but this keeps the unit testable.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.scrim} onClick={onScrimClick} data-state={open ? 'open' : 'closed'}>
      <div
        ref={panelRef}
        className={[styles.panel, className].filter(Boolean).join(' ')}
        style={{ width: `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
            data-drawer-close
          >
            <CloseGlyph />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function CloseGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 3 L11 11 M11 3 L3 11"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
